/**
 * Formatea una fecha según un formato definido. Unix timestamp como tipo number.
 * @param {Date|string|number} date Fecha a formatear. Unix en caso de date numérico.
 * @param {string} format Formato (PHP), por defecto 'Y-m-d'.
 * @param {any} defaultValue Valor devuelto si la fecha es no valida, por defecto string vacío.
 * @returns Retorna la fecha con el formato definido, en caso que la fecha sea no valida retorna defaultValue.
 */
export function formatDate(date, format = 'Y-m-d', defaultValue = '') {
    date = parseDate(date)
    if (date !== null) {
        const year = date.getFullYear()
        const month = date.getMonth()
        const day = date.getDate()
        const weekDay = date.getDay()
        let result = ''
        format.split('').forEach((char) => {
            let value = ''
            switch (char) {
                case 'Y':
                    value = year
                    break
                case 'y':
                    value = String(year).padStart(4, '0')
                    break
                case 'm':
                    value = String(month + 1).padStart(2, '0')
                    break
                case 'd':
                    value = String(day).padStart(2, '0')
                    break
                case 'H':
                    value = String(date.getHours()).padStart(2, '0')
                    break
                case 'i':
                    value = String(date.getMinutes()).padStart(2, '0')
                    break
                case 's':
                    value = String(date.getSeconds()).padStart(2, '0')
                    break
                case 'n':
                    value = month + 1
                    break
                case 'M':
                    value = mesesCorto[month]
                    break
                case 'F':
                    value = meses[month]
                    break
                case 'j':
                    value = day
                    break
                case 'w':
                    value = weekDay
                    break
                case 'N':
                    value = weekDay === 0 ? 7 : weekDay
                    break
                case 'D':
                    value = diasCorto[weekDay]
                    break
                case 'l':
                    value = dias[weekDay]
                    break
                default:
                    value = char
                    break
            }
            result = `${result}${value}`
        })
        return result
    } else {
        return defaultValue
    }
}

/**
 * Convierte un dato a tipo Date.
 * @ignore
 * @param {Date | string | number} date Dato a convertir a Date. Unix timestamp como tipo number.
 * @param {any} defaultValue Valor por defecto devuelto en caso que el Date sea no valido, por defecto null.
 * @returns Retorna fecha de tipo Date, en caso que la fecha sea no valida retorna defaultValue.
 */
export const parseDate = (date, defaultValue = null) => {
    let result = date
    if (typeof date === 'number') {
        result = new Date(date * 1000)
    } else if (typeof date === 'string') {
        result = new Date(date.includes(':') ? date : `${date} 00:00:00`)
    }
    return isValidDate(result) ? result : defaultValue
}

/**
 * Verifica si un dato es de tipo Date valido.
 * @param {any} date
 * @returns true si es un Date valido, false en caso contrario.
 */
export function isValidDate(date) {
    return date instanceof Date && !isNaN(date)
}

/**
 * Obtiene el nombre del mes.
 * @param {number} month Número del mes donde 1 es Enero y 12 es Diciembre.
 * @param {boolean} short Especifica si el nombre del mes sera corto o no.
 * @returns Retorna el nombre del mes.
 */
export function monthName(month, short = false) {
    return (short ? mesesCorto[month + 1] : meses[month + 1]) || ''
}
/**
 * Obtiene el nombre del día.
 * @param {number} day Número del día donde 1 es Lunes y 7 es Domingo.
 * @param {boolean} short Especifica si el nombre del día sera corto o no.
 * @returns Retorna el nombre del día.
 */
export function dayName(day, short = false) {
    day = parseInt(day)
    day = day > 0 && day <= 7 ? (day === 7 ? 0 : day) : null
    return (short ? diasCorto[day] : dias[day]) || ''
}

/**
 * Adiciona una cantidad de tiempo a una fecha.
 * @param {Date | string | number} date Fecha. Unix timestamp como tipo number.
 * @param {number} time Cantidad de tiempo a adicionar a la fecha.
 * @param {'seconds' | 'minutes' | 'hours' | 'days'} period Periodo de tiempo a adicionar, por defecto seconds.
 * @returns
 */
export function addTime(date, time, period = 'seconds') {
    let result = null
    const _date = parseDate(date)
    if (_date) {
        // Convertir date a unix timestamp y sumar el tiempo.
        const _time = Math.floor(_date.getTime() / 1000) + time * periodSeconds[period]
        // Convertir unix timestamp a date.
        result = new Date(_time * 1000)
    }
    return result
}

/**
 * Resta una cantidad de tiempo a una fecha.
 * @param {Date | string | number} date Fecha. Unix timestamp como tipo number.
 * @param {number} time Cantidad de tiempo a restar a la fecha.
 * @param {'seconds' | 'minutes' | 'hours' | 'days'} period Periodo de tiempo a restar, por defecto seconds.
 * @returns
 */
export function subtractTime(date, time, period = 'seconds') {
    let result = null
    const _date = parseDate(date)
    if (_date) {
        // Convertir date a unix timestamp y restar el tiempo.
        const _time = Math.floor(_date.getTime() / 1000) - time * periodSeconds[period]
        // Convertir unix timestamp a date.
        result = new Date(_time * 1000)
    }
    return result
}

/**
 * Adiciona a una fecha con el tiempo hh:mm:ss pasado como parametro.
 * @param {Date | string | number} date Fecha a aumentar la hora. Unix timestamp como tipo number.
 * @param {string} time Hora a agregar a la fecha en formato HH:MM o HH:MM:SS.
 * @param {any} defaultValue Valor por defecto devuelto si los params date, time o el resultado es no valido, por defecto null.
 * @return Fecha con la hora definida, en caso que la fecha sea no valida retorna defaultValue.
 */
export const parseDateTime = (date, time, defaultValue = null) => {
    let _date = parseDate(date)
    if (!_date) {
        if (typeof time === 'string' && regexTime.test(time)) {
            const times = time.split(':')
            _date.setUTCHours(times[0])
            _date.setUTCMinutes(times[1])
            _date.setUTCSeconds(times.length > 2 ? times[2] || 0 : 0)
        } else {
            _date = null
        }
    }
    return isValidDate(_date) ? _date : defaultValue
}

const periodSeconds = {
    seconds: 1,
    minutes: 60,
    hours: 3600,
    days: 86400,
}

const regexTime = /^\d{1,2}:\d{1,2}(:(\d{1,2}(\.\d{0,4})?)?)?$/

const meses = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
]
const mesesCorto = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const diasCorto = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa']
